import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Row, Col } from 'react-bootstrap';
import { FiArrowLeft, FiEdit2 } from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DocumentUploadForm from './DocumentUploadForm';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import axiosInstance from '../../services/AxiosInstance';
import { buildDocumentSharePayload } from '../../utils/documentSharePayload';
import { listStaffByCorporate } from '../../services/StaffService';
import { listVehiclesByCorporate } from '../../services/VehicleService';
import { useDocumentAccess } from '../../hooks/useDocumentAccess';
import { isValidDocumentField } from '../../utils/specialCharValidation';
import './commonstyle.css';

const EditDocument = () => {
  const navigate = useNavigate();
  const { documentId } = useParams();
  const { hasAccess, loading: accessLoading } = useDocumentAccess();

  // Form state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [dynamicFields, setDynamicFields] = useState({});
  const [isPrivate, setIsPrivate] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedSubDepartments, setSelectedSubDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [filteredDepartments, setFilteredDepartments] = useState([]);
  const [filteredSubDepartments, setFilteredSubDepartments] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [documentClassifications, setDocumentClassifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingDocument, setFetchingDocument] = useState(true);
  
  // Document fields
  const [documentTitle, setDocumentTitle] = useState('');
  const [documentRegisteredTo, setDocumentRegisteredTo] = useState('');
  const [documentReferenceNumber, setDocumentReferenceNumber] = useState('');
  const [documentIssueDate, setDocumentIssueDate] = useState('');
  const [documentExpiryDate, setDocumentExpiryDate] = useState('');
  const [remindMeBefore, setRemindMeBefore] = useState('');
  const [documentIssuingAuthority, setDocumentIssuingAuthority] = useState('');
  const [otherComments, setOtherComments] = useState('');
  const [documentData, setDocumentData] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({
    documentTitle: '',
    documentRegisteredTo: '',
    documentReferenceNumber: '',
    documentIssuingAuthority: '',
    otherComments: ''
  });
  
  // Document linking fields
  const [linkToStaff, setLinkToStaff] = useState(false);
  const [staffId, setStaffId] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [linkToVehicle, setLinkToVehicle] = useState(false);
  const [vehicleId, setVehicleId] = useState(null);
  const [vehicleList, setVehicleList] = useState([]);

  useEffect(() => {
    if (documentId) {
      fetchInitialData();
    }
  }, [documentId]);

  // Fetch staff list when linkToStaff is checked
  useEffect(() => {
    if (linkToStaff && staffList.length === 0) {
      fetchStaffList();
    }
  }, [linkToStaff]);

  // Fetch vehicle list when linkToVehicle is checked
  useEffect(() => {
    if (linkToVehicle && vehicleList.length === 0) {
      fetchVehicleList();
    }
  }, [linkToVehicle]);

  // Effect to set category once both document data and classifications are loaded
  useEffect(() => {
    if (documentData && documentClassifications.length > 0) {
      // Set category if not already set
      const categoryId = documentData.category_id || documentData.category?.id;
      if (categoryId && !selectedCategory) {
        console.log('Setting category:', categoryId, 'from classifications:', documentClassifications.length);
        setSelectedCategory(categoryId);
      }
    }
  }, [documentData, documentClassifications, selectedCategory]);

  const fetchInitialData = async () => {
    try {
      setFetchingDocument(true);
      // Fetch classifications first, then document details
      await Promise.all([
        fetchDocumentClassifications(),
        fetchBranches(),
        fetchDepartments(),
        fetchSubDepartments(),
        fetchUsers()
      ]);
      // Fetch document details after classifications are loaded
      await fetchDocumentDetails();
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load document data');
    } finally {
      setFetchingDocument(false);
    }
  };

  const fetchDocumentDetails = async () => {
    try {
      const response = await axiosInstance.get(`/documents/${documentId}`);
      const data = response.data;
      
      // Handle nested structure
      let document;
      if (data.success && data.data) {
        document = data.data;
      } else {
        document = data.data || data;
      }

      setDocumentData(document);

      console.log('Document data received:', document);

      // Prefill form fields - handle null/undefined values properly
      const title = document.document_title || document.name || '';
      const registeredTo = document.registered_to ?? '';
      const referenceNumber = document.reference_number ?? '';
      const issueDate = document.issue_date ? new Date(document.issue_date).toISOString().split('T')[0] : '';
      const expiryDate = document.expiry_date ? new Date(document.expiry_date).toISOString().split('T')[0] : '';
      const remindBefore = document.remind_before ?? '';
      const issuingAuthority = document.issuing_authority ?? '';
      const comments = document.other_comments ?? '';
      
      console.log('Setting form fields:', {
        title,
        registeredTo,
        referenceNumber,
        issueDate,
        expiryDate,
        remindBefore,
        issuingAuthority,
        comments,
        categoryId: document.category_id || document.category?.id,
        isPrivate: document.is_private
      });

      // Set all fields
      setDocumentTitle(title);
      setDocumentRegisteredTo(registeredTo);
      setDocumentReferenceNumber(referenceNumber);
      setDocumentIssueDate(issueDate);
      setDocumentExpiryDate(expiryDate);
      setRemindMeBefore(remindBefore);
      setDocumentIssuingAuthority(issuingAuthority);
      setOtherComments(comments);
      
      // Set privacy
      setIsPrivate(document.is_private ?? false);

      // Set branches
      if (document.branches && document.branches.length > 0) {
        const branchIds = document.branches.map(b => b.id || b.branch_id).filter(Boolean);
        setSelectedBranches(branchIds);
      } else if (document.branch_id) {
        setSelectedBranches([document.branch_id]);
      }

      // Set departments
      if (document.departments && document.departments.length > 0) {
        const departmentIds = document.departments.map(d => d.id || d.department_id).filter(Boolean);
        setSelectedDepartments(departmentIds);
      } else if (document.department_id) {
        setSelectedDepartments([document.department_id]);
      }

      // Set sub-departments
      if (document.subdepartments && document.subdepartments.length > 0) {
        const subDepartmentIds = document.subdepartments.map(sd => sd.id || sd.sub_department_id).filter(Boolean);
        setSelectedSubDepartments(subDepartmentIds);
      } else if (document.sub_departments && document.sub_departments.length > 0) {
        const subDepartmentIds = document.sub_departments.map(sd => sd.id || sd.sub_department_id).filter(Boolean);
        setSelectedSubDepartments(subDepartmentIds);
      } else if (document.subdepartment_id) {
        setSelectedSubDepartments([document.subdepartment_id]);
      }

      // Set users
      if (document.users && document.users.length > 0) {
        const userIds = document.users.map(u => u.id || u.user_id || u.CorporateUserId).filter(Boolean);
        setSelectedUsers(userIds);
      }

      // Set dynamic fields if available
      if (document.field_values) {
        try {
          const fieldValues = typeof document.field_values === 'string' 
            ? JSON.parse(document.field_values) 
            : document.field_values;
          setDynamicFields(fieldValues);
        } catch (e) {
          console.error('Error parsing field_values:', e);
        }
      }

      // Set document linking fields
      const isLinkedToStaff = document.is_linked_to_staff || document.link_to_staff || false;
      const docStaffId = document.staff_id || document.linked_staff?.id || null;
      const isLinkedToVehicle = document.is_linked_to_vehicle || document.link_to_vehicle || false;
      const docVehicleId = document.vehicle_id || document.linked_vehicle?.id || null;

      setLinkToStaff(isLinkedToStaff);
      setStaffId(docStaffId);
      setLinkToVehicle(isLinkedToVehicle);
      setVehicleId(docVehicleId);

      // Fetch staff/vehicle lists if linked
      if (isLinkedToStaff && docStaffId) {
        fetchStaffList();
      }
      if (isLinkedToVehicle && docVehicleId) {
        fetchVehicleList();
      }
    } catch (error) {
      console.error('Error fetching document details:', error);
      toast.error('Failed to load document details');
      setTimeout(() => {
        navigate('/documents/mydocuments');
      }, 2000);
    }
  };

  const fetchDocumentClassifications = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const response = await axiosInstance.get(`/document-categories?corporate_id=${corporateId}`);
      const data = response.data;
      const classifications = data.data || data || [];
      setDocumentClassifications(classifications);
    } catch (error) {
      console.error('Error fetching document classifications:', error);
    }
  };

  const fetchBranches = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const res = await axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`);
      const data = res.data;
      if (res) {
        setBranches(data.data || data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const res = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}`);
      const data = res.data;
      if (res) {
        const deptList = data.data || data || [];
        setDepartments(deptList);
        setFilteredDepartments(deptList);
      }
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchSubDepartments = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      let res;
      try {
        res = await axiosInstance.get(`/settings/subDepartments?corporate_id=${corporateId}`);
      } catch (e1) {
        res = await axiosInstance.get(`/settings/subdepartments?corporate_id=${corporateId}`);
      }
      const data = res.data;
      if (res) {
        const subDeptList = data.data || data || [];
        setSubDepartments(subDeptList);
        setFilteredSubDepartments(subDeptList);
      }
    } catch (error) {
      console.error('Error fetching sub-departments:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      
      const params = new URLSearchParams();
      params.append('corporateId', corporateId);
      
      const response = await axiosInstance.get(`/list/corporateuserslist?${params.toString()}`);
      const users = response.data.users || [];
      setAvailableUsers(users);
      setFilteredUsers(users);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchStaffList = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      if (!corporateId) return;
      
      const res = await listStaffByCorporate(corporateId, { limit: 1000 });
      if (res.data.success) {
        setStaffList(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching staff list:', error);
    }
  };

  const fetchVehicleList = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      if (!corporateId) return;
      
      const res = await listVehiclesByCorporate(corporateId, { limit: 1000 });
      if (res.data.success) {
        setVehicleList(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching vehicle list:', error);
    }
  };

  // Handle branch change - filter departments
  const handleBranchChange = (branchIds, selectedOptions) => {
    setSelectedBranches(branchIds);
    
    if (branchIds.length > 0) {
      const filtered = departments.filter(dept => branchIds.includes(dept.branch_id));
      setFilteredDepartments(filtered);
      
      // Filter selected departments to keep only valid ones
      const validDepartmentIds = selectedDepartments.filter(deptId => {
        return filtered.some(d => d.id === deptId);
      });
      setSelectedDepartments(validDepartmentIds);
      
      // Filter sub-departments based on valid departments
      const subs = subDepartments.filter(sd => validDepartmentIds.includes(sd.department_id));
      setFilteredSubDepartments(subs);
      
      // Keep only valid sub-departments
      const validSubDepartmentIds = selectedSubDepartments.filter(subDeptId => {
        return subs.some(sd => (sd.id || sd.sub_department_id) === subDeptId);
      });
      setSelectedSubDepartments(validSubDepartmentIds);
    } else {
      setFilteredDepartments(departments);
      setFilteredSubDepartments(subDepartments);
    }
  };

  // Handle department change - filter sub-departments
  const handleDepartmentChange = (departmentIds, selectedOptions) => {
    setSelectedDepartments(departmentIds);
    
    if (departmentIds.length > 0) {
      const subs = subDepartments.filter(sd => departmentIds.includes(sd.department_id));
      setFilteredSubDepartments(subs);
      
      // Keep only valid sub-departments
      const validSubDepartmentIds = selectedSubDepartments.filter(subDeptId => {
        return subs.some(sd => (sd.id || sd.sub_department_id) === subDeptId);
      });
      setSelectedSubDepartments(validSubDepartmentIds);
    } else {
      setFilteredSubDepartments(subDepartments);
    }
  };

  // Handle sub-department change
  const handleSubDepartmentChange = (subDepartmentIds, selectedOptions) => {
    setSelectedSubDepartments(subDepartmentIds);
  };

  // Handle user change
  const handleUserChange = (userIds, selectedOptions) => {
    setSelectedUsers(userIds);
  };

  const handleSubmit = async () => {
    // Validate linking fields
    if (linkToStaff && !staffId) {
      toast.error('Please select a staff member when "Link to Staff" is checked');
      return;
    }

    if (linkToVehicle && !vehicleId) {
      toast.error('Please select a vehicle when "Link to Vehicle" is checked');
      return;
    }

    let hasError = false;
    const newErrors = { documentTitle: '', documentRegisteredTo: '', documentReferenceNumber: '', documentIssuingAuthority: '', otherComments: '' };
    if (!documentTitle.trim()) {
      newErrors.documentTitle = 'Document title is required';
      hasError = true;
    } else if (!isValidDocumentField(documentTitle)) {
      newErrors.documentTitle = 'Document title cannot contain only special characters.';
      hasError = true;
    }
    if (documentRegisteredTo.trim() && !isValidDocumentField(documentRegisteredTo)) {
      newErrors.documentRegisteredTo = 'Cannot contain only special characters.';
      hasError = true;
    }
    if (documentReferenceNumber.trim() && !isValidDocumentField(documentReferenceNumber)) {
      newErrors.documentReferenceNumber = 'Cannot contain only special characters.';
      hasError = true;
    }
    if (documentIssuingAuthority.trim() && !isValidDocumentField(documentIssuingAuthority)) {
      newErrors.documentIssuingAuthority = 'Cannot contain only special characters.';
      hasError = true;
    }
    if (otherComments.trim() && !isValidDocumentField(otherComments)) {
      newErrors.otherComments = 'Cannot contain only special characters.';
      hasError = true;
    }
    if (hasError) {
      setFieldErrors(newErrors);
      return;
    }

    // Validate required dynamic fields only if a classification is selected
    if (selectedCategory) {
      const selectedCategoryData = documentClassifications.find(cat => cat.id === selectedCategory);
      if (selectedCategoryData && selectedCategoryData.fields) {
        const requiredFields = selectedCategoryData.fields.filter(field => field.is_required);
        const missingFields = requiredFields.filter(field => {
          const fieldValue = dynamicFields[field.id];
          return !fieldValue || fieldValue.trim() === '';
        });
        
        if (missingFields.length > 0) {
          toast.error(`Please fill in all required fields: ${missingFields.map(f => f.field_name).join(', ')}`);
          return;
        }
      }
    }

    setLoading(true);
    
    try {
      // Build JSON payload
      const payload = {
        document_title: documentTitle
      };
      
      // Optional document fields
      if (documentRegisteredTo) {
        payload.registered_to = documentRegisteredTo;
      }
      if (documentReferenceNumber) {
        payload.reference_number = documentReferenceNumber;
      }
      if (documentIssueDate) {
        payload.issue_date = documentIssueDate;
      }
      if (documentExpiryDate) {
        payload.expiry_date = documentExpiryDate;
      }
      if (remindMeBefore) {
        payload.remind_before = remindMeBefore;
      }
      if (documentIssuingAuthority) {
        payload.issuing_authority = documentIssuingAuthority;
      }
      if (otherComments) {
        payload.other_comments = otherComments;
      }
      
      // Category
      if (selectedCategory) {
        payload.category_id = selectedCategory;
      }
      
      // Dynamic fields
      if (Object.keys(dynamicFields).length > 0) {
        payload.field_values = dynamicFields;
      }
      
      // Privacy and access
      payload.is_private = isPrivate;
      
      // Document linking fields
      payload.link_to_staff = linkToStaff;
      if (linkToStaff && staffId) {
        payload.staff_id = staffId;
      } else {
        payload.staff_id = null;
      }

      payload.link_to_vehicle = linkToVehicle;
      if (linkToVehicle && vehicleId) {
        payload.vehicle_id = vehicleId;
      } else {
        payload.vehicle_id = null;
      }
      
      // Apply hierarchical sharing rules
      const sharePayload = buildDocumentSharePayload(
        selectedBranches,
        selectedDepartments,
        selectedSubDepartments,
        selectedUsers
      );
      Object.assign(payload, sharePayload);

      console.log('Sending PUT request with payload:', payload);

      const response = await axiosInstance.put(`/documents/${documentId}`, payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 200 || response.status === 204) {
        toast.success('Document updated successfully');
        
        // Navigate back after successful update
        setTimeout(() => {
          navigate(`/documents/details/${documentId}`);
        }, 2000);
      }
    } catch (error) {
      console.error('Error updating document:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to update document';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (documentId) {
      navigate(`/documents/details/${documentId}`);
    } else {
      navigate('/documents/mydocuments');
    }
  };

  // Update filtered departments/sub-departments when document data loads
  useEffect(() => {
    if (selectedBranches.length > 0 && departments.length > 0) {
      const filtered = departments.filter(dept => selectedBranches.includes(dept.branch_id));
      setFilteredDepartments(filtered);
      
      if (selectedDepartments.length > 0) {
        const subs = subDepartments.filter(sd => selectedDepartments.includes(sd.department_id));
        setFilteredSubDepartments(subs);
      }
    } else if (departments.length > 0) {
      setFilteredDepartments(departments);
      setFilteredSubDepartments(subDepartments);
    }
  }, [selectedBranches, selectedDepartments, departments, subDepartments]);

  // Show loading while checking access
  if (accessLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Don't render if no access (will redirect)
  if (!hasAccess) {
    return null;
  }

  if (fetchingDocument) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ToastContainer />
      <div className="d-flex align-items-center mb-3">
        <button
          className="back-button"
          onClick={handleCancel}
        >
          <FiArrowLeft style={{ marginRight: '0.5rem' }} />
          Back
        </button>
      </div>
      
      <Row>
        <Col lg={12}>
          <Card>
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">
                  <FiEdit2 className="me-2" />
                  Edit Document
                </h4>
              </div>
            </Card.Header>
            <Card.Body>
              {documentData && (
                <div className="mb-3">
                  <small className="text-muted">
                    Editing: <strong>{documentData.name || documentData.file_name}</strong>
                    {documentData.file_size && (
                      <span> ({(parseFloat(documentData.file_size) / 1024 / 1024).toFixed(2)} MB)</span>
                    )}
                  </small>
                </div>
              )}
              
              <DocumentUploadForm
                key={`edit-form-${documentId}-${documentData ? 'loaded' : 'loading'}`}
                files={[]} // No file upload in edit mode
                onFilesChange={() => {}} // No-op
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                folderId={documentData?.document_path_id || null}
                categories={documentClassifications}
                dynamicFields={dynamicFields}
                onDynamicFieldsChange={setDynamicFields}
                isPrivate={isPrivate}
                onIsPrivateChange={setIsPrivate}
                selectedBranches={selectedBranches}
                onBranchesChange={handleBranchChange}
                selectedDepartments={selectedDepartments}
                onDepartmentsChange={handleDepartmentChange}
                selectedSubDepartments={selectedSubDepartments}
                onSubDepartmentsChange={handleSubDepartmentChange}
                branches={branches}
                departments={departments}
                subDepartments={subDepartments}
                filteredDepartments={filteredDepartments}
                filteredSubDepartments={filteredSubDepartments}
                availableUsers={filteredUsers}
                selectedUsers={selectedUsers}
                onUsersChange={handleUserChange}
                documentTitle={documentTitle}
                onDocumentTitleChange={(v) => { setDocumentTitle(v); setFieldErrors(prev => ({ ...prev, documentTitle: '' })); }}
                documentTitleError={fieldErrors.documentTitle}
                documentRegisteredTo={documentRegisteredTo}
                onDocumentRegisteredToChange={(v) => { setDocumentRegisteredTo(v); setFieldErrors(prev => ({ ...prev, documentRegisteredTo: '' })); }}
                documentRegisteredToError={fieldErrors.documentRegisteredTo}
                documentReferenceNumber={documentReferenceNumber}
                onDocumentReferenceNumberChange={(v) => { setDocumentReferenceNumber(v); setFieldErrors(prev => ({ ...prev, documentReferenceNumber: '' })); }}
                documentReferenceNumberError={fieldErrors.documentReferenceNumber}
                documentIssueDate={documentIssueDate}
                onDocumentIssueDateChange={setDocumentIssueDate}
                documentExpiryDate={documentExpiryDate}
                onDocumentExpiryDateChange={setDocumentExpiryDate}
                remindMeBefore={remindMeBefore}
                onRemindMeBeforeChange={setRemindMeBefore}
                documentIssuingAuthority={documentIssuingAuthority}
                onDocumentIssuingAuthorityChange={(v) => { setDocumentIssuingAuthority(v); setFieldErrors(prev => ({ ...prev, documentIssuingAuthority: '' })); }}
                documentIssuingAuthorityError={fieldErrors.documentIssuingAuthority}
                otherComments={otherComments}
                onOtherCommentsChange={(v) => { setOtherComments(v); setFieldErrors(prev => ({ ...prev, otherComments: '' })); }}
                otherCommentsError={fieldErrors.otherComments}
                duplicateFiles={[]}
                checkingDuplicates={false}
                hideFileUpload={true}
                linkToStaff={linkToStaff}
                onLinkToStaffChange={setLinkToStaff}
                staffId={staffId}
                onStaffIdChange={setStaffId}
                staffList={staffList}
                linkToVehicle={linkToVehicle}
                onLinkToVehicleChange={setLinkToVehicle}
                vehicleId={vehicleId}
                onVehicleIdChange={setVehicleId}
                vehicleList={vehicleList}
              />
            </Card.Body>
            <Card.Footer>
              <div className="d-flex justify-content-end gap-2">
                <Button className="doc-form-cancel-btn" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button 
                  className="doc-form-submit-btn"
                  onClick={handleSubmit}
                  disabled={loading || !documentTitle.trim()}
                >
                  {loading ? 'Updating...' : 'Update Document'}
                </Button>
              </div>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      <InternalFooter />
    </div>
  );
};

export default EditDocument;

